import PageLoader from '@components/PageLoader'
import { ErrorNote } from '@components/Notice'
import { EmptyNote } from '@components/EmptyState'
import Segmented from '@components/Segmented'
import { useAdminFunnelStep } from '@hooks/useAdminFunnelStep'
import type { AdminFunnelStepRow } from '@orbit/api-types'
import { useAdminFilter } from '@pages/Admin/AdminFilterContext'
import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { AdminHeading } from '../AdminLayout.styles'
import { BackLink } from '../adminDetail.styles'
import {
	DeletedTag,
	InternalTag,
	Muted,
	PageButton,
	PageStatus,
	Pagination,
	Row,
	StaticRow,
	SubText,
	Table,
	TableWrap,
	Td,
	Th,
	Toolbar,
} from '../adminList.styles'
import { formatDateTime, formatName } from '../adminFormat'
import { rangeLabel } from '../dateRange'
import { CohortNote } from '../Onboarding/OnboardingPage.styles'
import {
	ACTION_LABELS,
	formatElapsed,
	FUNNEL_STEPS,
	funnelPath,
	PLATFORM_OPTIONS,
	stepCountLabel,
	stepFromParam,
	TRIP_ORIGIN_LABELS,
	versionLabel,
} from './funnelView'
import { usePlatformParam } from './usePlatformParam'

const PAGE_SIZE = 25

/**
 * A step the row reached: when, and how long after the first open. `label` names
 * what happened where the column alone doesn't (created or joined, which action).
 */
const StepCell: React.FC<{ label?: string; firstSeenAt: number; at: number }> = ({ label, firstSeenAt, at }) => {
	const elapsed = formatElapsed(firstSeenAt, at)
	return (
		<Td>
			{label ?? formatDateTime(at)}
			<SubText>{label === undefined ? elapsed : `${formatDateTime(at)} · ${elapsed}`}</SubText>
		</Td>
	)
}

const NotReached: React.FC = () => (
	<Td>
		<Muted>—</Muted>
	</Td>
)

/**
 * The devices and people behind one funnel step count (ADR 0010), for the same
 * range, platform and internal toggle as the funnel, so the list holds exactly as
 * many rows as the count that opened it.
 *
 * The first step lists devices, most of which never sign up; every later step
 * lists people, each on the device they are attributed to. Each row says how far
 * it got and how long after the first open, and a person's row opens their user
 * page.
 */
const FunnelStepPage: React.FC = () => {
	const navigate = useNavigate()
	const { step: stepParam } = useParams<{ step: string }>()
	const [platform, setPlatform] = usePlatformParam()
	const { from, to, includeInternal } = useAdminFilter()
	const step = stepFromParam(stepParam)
	const [page, setPage] = useState(1)

	// A new range, platform, step or internal toggle is a new list, so start it at page 1.
	useEffect(() => {
		setPage(1)
	}, [from, to, platform, step, includeInternal])

	const { data, isLoading, isError } = useAdminFunnelStep(step, platform, page, PAGE_SIZE)

	if (step === null) return <Navigate to={funnelPath(platform)} replace />

	const label = FUNNEL_STEPS.find(candidate => candidate.key === step)?.label
	const showPlatform = platform === 'all'
	const columns = showPlatform ? 7 : 6

	const renderRow = (row: AdminFunnelStepRow) => {
		const { person, firstTrip, firstAction } = row
		const cells = (
			<>
				<Td>
					{person === null ? (
						<Muted>No sign-up</Muted>
					) : (
						<>
							{formatName(person.firstName, person.lastName)}
							{person.isInternal && <InternalTag>Internal</InternalTag>}
							{person.isDeleted && <DeletedTag>Deleted</DeletedTag>}
							<SubText>{person.email}</SubText>
						</>
					)}
				</Td>
				{showPlatform && <Td>{row.platform}</Td>}
				<Td>{versionLabel(row.platform, row.appVersion)}</Td>
				<Td>{formatDateTime(row.firstSeenAt)}</Td>
				{person === null ? (
					<NotReached />
				) : (
					<StepCell firstSeenAt={row.firstSeenAt} at={person.signedUpAt} />
				)}
				{firstTrip === null ? (
					<NotReached />
				) : (
					<StepCell
						label={TRIP_ORIGIN_LABELS[firstTrip.origin]}
						firstSeenAt={row.firstSeenAt}
						at={firstTrip.at}
					/>
				)}
				{firstAction === null ? (
					<NotReached />
				) : (
					<StepCell
						label={ACTION_LABELS[firstAction.type]}
						firstSeenAt={row.firstSeenAt}
						at={firstAction.at}
					/>
				)}
			</>
		)

		return person === null ? (
			<StaticRow key={row.deviceId}>{cells}</StaticRow>
		) : (
			<Row
				key={row.deviceId}
				$deleted={person.isDeleted}
				onClick={() => navigate(`/admin/users/${person.id}`)}
			>
				{cells}
			</Row>
		)
	}

	return (
		<>
			<BackLink to={funnelPath(platform)}>← Back to funnel</BackLink>
			<AdminHeading>{label}</AdminHeading>

			<Toolbar>
				<Segmented
					aria-label="Platform"
					options={PLATFORM_OPTIONS}
					value={platform}
					onChange={setPlatform}
				/>
			</Toolbar>

			{isLoading ? (
				<PageLoader label="Loading…" />
			) : isError || !data ? (
				<ErrorNote>Could not load this step. Please try again.</ErrorNote>
			) : (
				<>
					<CohortNote>
						{stepCountLabel(step, data.total)} · devices first seen {rangeLabel(from, to)} ·
						every step within {data.windowDays} days of the first open, with the time since it
					</CohortNote>

					<TableWrap>
						<Table>
							<thead>
								<tr>
									<Th>Person</Th>
									{showPlatform && <Th>Platform</Th>}
									<Th>Version</Th>
									<Th>First seen</Th>
									<Th>Signed up</Th>
									<Th>First trip</Th>
									<Th>First planning action</Th>
								</tr>
							</thead>
							<tbody>
								{data.rows.length === 0 ? (
									<tr>
										<Td colSpan={columns}>
											<EmptyNote>Nobody reached this step.</EmptyNote>
										</Td>
									</tr>
								) : (
									data.rows.map(renderRow)
								)}
							</tbody>
						</Table>
					</TableWrap>

					<Pagination>
						<PageStatus>
							Page {data.page} of {data.totalPages}
						</PageStatus>
						<PageButton disabled={data.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>
							Previous
						</PageButton>
						<PageButton disabled={data.page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
							Next
						</PageButton>
					</Pagination>
				</>
			)}
		</>
	)
}

export default FunnelStepPage
